<template>
    <div>
        <div>
            <form :method="paymentFormMethod" :action="paymentUrl" ref="paymentForm">
                <input type="hidden" :name="field" :value="value" v-for="(value, field) in paymentParams" :key="field">
            </form>
        </div>
        <progress-header />

        <div class="container mt-4">
            <div class="row">
                <template v-if="isSending">
                    <div class="col-12 mb-2 d-flex align-items-center justify-content-center preloader-bg">
                        <div class="bg-white shadow p-5 text-center">
                            <div>
                                <div class="spinner-border">
                                    <span claspurchaser_s="sr-only" />
                                </div>
                            </div>

                            <div class="text-lg mt-2">
                                {{ $t('taxi.pages.summary.creating_order') }}
                            </div>

                            <div class="text-sm mt-3" v-if="hasPayment">
                                {{ $t('taxi.pages.summary.creating_order_redirect') }}
                            </div>

                            <h4 class="mt-5" v-if="!this.queryResolved && this.longWaitInfo" v-html="this.longWaitInfo" />
                        </div>
                    </div>
                </template>
                <template v-else>
                    <form class="col-12 col-lg-7" @submit.prevent="handleSubmit">
                        <div class="card shadow-sm">
                            <div class="px-4 py-3 border-bottom">
                                <h4 class="text-blue-600 font-weight-bold">
                                    {{ $t('taxi.pages.summary.header') }}
                                </h4>
                            </div>

                            <div class="px-4 py-3">
                                <div class="text-blue-600 font-weight-bold">
                                    {{ $t('passengers') }}
                                </div>

                                <div class="py-3 border-bottom" v-for="(passenger, idx) in passengers" :key="idx">
                                    {{ passenger.first_name }} {{ passenger.last_name }}
                                </div>

                                <div class="mt-4 mb-3 text-blue-600 font-weight-bold">
                                    {{ $t('purchaser_details') }}
                                </div>

                                <summary-purchaser />

                                <div class="mt-4 mb-3 text-blue-600 font-weight-bold">
                                    {{ $t('invoice') }}
                                </div>

                                <div>
                                    <label class="m-0">
                                        <input
                                            type="checkbox"
                                            class="mr-2"
                                            :checked="isInvoice"
                                            @change="onIsInvoiceInput"
                                        >

                                        {{ $t('taxi.pages.summary.is_invoice') }}
                                    </label>
                                </div>

                                <summary-invoice class="mt-2" v-if="isInvoice" />

                                <div class="mt-4 text-sm">
                                    <div class="mt-4 text-sm">
                                        <div class="mb-2">
                                            {{ $t('airport_transfers.pages.summary.consents_text') }}
                                        </div>

                                        <label class="mb-2">
                                            <input
                                                type="checkbox"
                                                class="mr-2"
                                                :checked="areAllConsentsChecked"
                                                v-if="consents"
                                                @change="markAllConsents"
                                            >

                                            {{ $t('consents.mark_all') }}
                                        </label>

                                        <div v-for="consent in consents" :key="consent.id">
                                            <label class="mb-1" :class="{'is-required': consent.required}">
                                                <input
                                                    type="checkbox"
                                                    class="mr-2"
                                                    :required="consent.required"
                                                    v-model="consent.checked"
                                                    @change="onConsentChange(consent, $event)"
                                                >

                                                <span v-html="consent.label" />
                                            </label>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div class="my-4">
                            <button type="submit" class="btn btn-primary px-4">
                                {{ $t('book_and_payment') }}
                            </button>
                        </div>
                    </form>
                </template>

                <div class="col-12 col-lg-5" v-if="!isSending">
                    <the-cart />
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    import { mapGetters } from 'vuex';
    import toastr from 'toastr';
    import TheCart from './components/TheCart';
    import SummaryPurchaser from './components/Summary/SummaryPurchaser';
    import SummaryInvoice from './components/Summary/SummaryInvoice';
    import ProgressHeader from './components/ProgressHeader';
    import {validateEmail} from '@utilities/validation';
    import ConsentRepository from '@repositories/ConsentRepository';
    import {trackFB, trackGA, trackMS} from '@utilities/tracking';
    import i18n from '@/i18n';
    import {AIRPORT_TRANSFERS_LONG_WAIT_MESSAGE_TIMEOUT} from '@/constants';
    import BackofficeRepository from '@repositories/BackofficeRepository';
    import * as Sentry from '@sentry/browser';
    import orderRepository from '@repositories/OrderRepository';

    export default {
        components: {
            TheCart,
            SummaryPurchaser,
            SummaryInvoice,
            ProgressHeader,
        },
        data() {
            return {
                hasPayment: false,
                paymentUrl: null,
                paymentFormMethod: null,
                paymentParams: null,
                isSending: false,
                markAllConsentsCheckboxValue: false,
                consents: null,
                queryResolved: false,
                longWaitInfo: null,

                backofficeOrder: null,
                backofficeError: null,
                backofficeOrderInProgress: false,
            };
        },
        computed: {
            ...mapGetters({
                passengers: 'airportTransfersSearch/getPassengers',
                isInvoice: 'airportTransfersSearch/getIsInvoice',
                invoice: 'airportTransfersSearch/getInvoice',
                departureTravel: 'airportTransfersSearch/getDepartureTravel',
                returnTravel: 'airportTransfersSearch/getReturnTravel',
                sessionId: 'airportTransfersSearch/getSessionID',
                searchData: 'airportTransfersSearch/getSearchParameters',
                purchaser: 'airportTransfersSearch/getPurchaser',
                createAccount: 'airportTransfersSearch/isCheckedCreateAccount',
                travelOptions: 'airportTransfersSearch/getTravelOptions',
                travelOptionComments: 'airportTransfersSearch/getTravelOptionComments',
                password: 'airportTransfersSearch/getPassword',
                passwordConfirmation: 'airportTransfersSearch/getPasswordConfirmation',
                defaultCurrencyCode: 'constants/getDefaultCurrencyCode',
                currency: 'airportTransfersSearch/getCurrency',
                totalCost: 'airportTransfersSearch/getTotalCost',
                isUserLoggedIn: 'user/isLoggedIn',
            }),

            areAllConsentsChecked() {
                if (!this.consents) {
                    return false;
                }

                let result = true;

                this.consents.forEach((consent) => {
                    if (!consent.checked) {
                        result = false;
                    }
                });

                return result;
            },
        },

        created() {
            trackGA('checkout_progress', {
                checkout_step: 6,
                checkout_option: window.location.pathname,
            });

            if(!this.searchData) {
                this.$router.push({ name: 'airport-transfers.index' });

                return;
            }
            ConsentRepository.list()
                .then((response) => {
                    this.consents = response.data.data;
                });
        },
        methods: {
            async addBackofficeOrder(cart, aCurrency, departureTravel, returnTravel) {
                console.debug(arguments);
                this.backofficeOrderInProgress = true;
                this.backofficeError = null;

                const currency = aCurrency.toLowerCase()==='pln'?'PLN':'EUR';
                const passengers = cart.passengers.map(i => {
                    return {
                        type: i.ticket_type==='normal'?'ADULT':'CHILD_BELOW_4',
                        firstName: i.first_name,
                        lastName: i.last_name,
                        phoneNumber: i.phone,
                    };
                });
                passengers[0].email = cart.purchaser.email;
                const vehicleRequired = BackofficeRepository.mapVehicleTypeEnum(cart.passengers.length);
                const rides = [{
                    ticketedDataLineId: departureTravel.lineRide.lineId,
                    ticketedDataPlannerRideId: departureTravel.lineRide.rideId,
                    ticketedDataStartStopPointId: departureTravel.lineRide.startStopPointId,
                    ticketedDataStartStopAt: departureTravel.lineRide.startDate,
                    ticketedDataEndStopPointId: departureTravel.lineRide.destinationStopPointId,
                    ticketedDataEndStopAt: departureTravel.lineRide.endDate,
                    vehicleRequired: vehicleRequired,
                    additionalOptions: cart.travel_options.filter(o => !!o.quantity).map(o => ({ id: o.id, count: o.quantity })),
                    stops: [],
                }];

                //configure ride back
                if(cart.search_data.with_return){
                    rides.push({
                        ticketedDataLineId: returnTravel.lineRide.lineId,
                        ticketedDataPlannerRideId: returnTravel.lineRide.rideId,
                        ticketedDataStartStopPointId: returnTravel.lineRide.startStopPointId,
                        ticketedDataStartStopAt: returnTravel.lineRide.startDate,
                        ticketedDataEndStopPointId: returnTravel.lineRide.destinationStopPointId,
                        ticketedDataEndStopAt: returnTravel.lineRide.endDate,
                        vehicleRequired: vehicleRequired,
                        additionalOptions: cart.travel_options.filter(o => !!o.quantity).map(o => ({ id: o.id, count: o.quantity })),
                        stops: [],
                    });
                }

                //edit user for correct invoice data
                // if(cart.is_invoice){
                //     await BackofficeRepository.editClientUserMe({
                //         'firstName': 'Follow me!',
                //         'lastName': 'Client',
                //         'phoneNumber': '914850422',
                //         'corporateName': cart.invoice.company,
                //         'addressStreet': cart.invoice.address,
                //         'addressPostal': cart.invoice.zip,
                //         'addressCity': cart.invoice.city,
                //         // 'nip': cart.invoice.vat_number,
                //         // 'email': cart.invoice.email,
                //     });
                // }

                //create order request
                const params = {
                    generatePayment: true,
                    requireInvoice: cart.is_invoice,
                    passengers: passengers,
                    comment: cart.travel_options_comments?cart.travel_options_comments:'',
                    rides: rides,
                    value: {currency: currency},
                };
                await BackofficeRepository.addOrderByClient('BUS_LINE', params, '/airport-transfers/confirmation').then(resp => {
                    this.backofficeOrder = resp.data.addOrderByClient;
                    this.backofficeOrderInProgress = false;
                }).catch(error => {
                    Sentry.withScope(scope => {
                        scope.setExtras({
                            error: error,
                            params: JSON.stringify(params),
                            tz: Intl.DateTimeFormat().resolvedOptions().timeZone,
                            localTime: new Date().toLocaleString('pl-PL', {timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone}),
                        });
                        Sentry.captureMessage('LINES addOrderByClient failed', 'log');
                    });
                    this.backofficeError = error.graphQLErrors;
                    this.backofficeOrderInProgress = false;
                });

                //revert anon client data to default
                // if(cart.is_invoice){
                //     await BackofficeRepository.editClientUserMeAnonDefault();
                // }
                return this.backofficeOrder?this.backofficeOrder:false;
            },

            scheduleLongWaitMessage() {
                if(false === AIRPORT_TRANSFERS_LONG_WAIT_MESSAGE_TIMEOUT){
                    return;
                }
                this.queryResolved = false;
                setTimeout(function(){
                    if(!this.queryResolved){
                        this.longWaitInfo = i18n.tc('maintenance_long_wait_info');
                    }
                }.bind(this), AIRPORT_TRANSFERS_LONG_WAIT_MESSAGE_TIMEOUT);
            },

            setQueryResolved() {
                this.queryResolved = true;
                this.longWaitInfo = null;
            },

            async handleSubmit(){
                //reset search if BO lineRide was not found
                if(!this.departureTravel.lineRide){
                    toastr.error(this.$t('taxi.pages.vehicles.no_transfers_found'));
                    await this.$store.dispatch('airportTransfersSearch/reset');
                    await this.$router.push({ name: 'airport-transfers.index' });
                    return;
                }

                //redirect to login page if token expired
                if(!await BackofficeRepository.checkUserTokenBeforeOrder(this.isUserLoggedIn)){
                    await this.$store.dispatch('user/unsetUser');
                    toastr.error(this.$t('auth.session_expired'));
                    this.$router.push({ name: 'login.index', query: {redirectTo: this.$route.fullPath} });
                    return;
                }

                if (this.createAccount && (this.password !== this.passwordConfirmation)) {
                    toastr.error(this.$t('auth.password_match_warning'));
                } else if(!validateEmail(this.purchaser.email)) {
                    toastr.error(this.$t('airport_transfers.pages.email_validation'));
                } else if(!this.validatePhone()) {
                    return false;
                } else if(!this.validateZipCode()) {
                    return false;
                } else {
                    this.isSending = true;

                    let checked_consents = [];

                    this.consents.forEach((consent) => {
                        if (consent.checked) {
                            checked_consents.push(consent.id);
                        }
                    });
                    let searchData = this.searchData;
                    let times = {
                        departureTime: this.departureTravel.times.departureTime,
                    };
                    if(this.searchData.with_return) {
                        times = {
                            ...times,
                            returnTime: this.returnTravel.times.departureTime,
                        };
                    }
                    searchData = {
                        ...searchData,
                        times: times,
                    };


                    let data = {
                        departure_ids: this.departureTravel.ids,
                        passengers: this.passengers,
                        session_id: this.sessionId,
                        search_data: searchData,
                        purchaser: this.purchaser,
                        is_invoice: this.isInvoice,
                        invoice: this.invoice,
                        create_account: this.createAccount,
                        travel_options: this.travelOptions,
                        travel_options_comments: this.travelOptionComments,
                        password: this.password,
                        password_confirmation: this.passwordConfirmation,
                        currency: this.currency,
                        checked_consents: checked_consents,
                    };

                    if(this.searchData.with_return) {
                        data = {
                            ...data,
                            return_ids: this.returnTravel.ids,
                        };
                    }

                    this.scheduleLongWaitMessage();
                    this.addBackofficeOrder(data, data.currency, this.departureTravel, this.returnTravel).then(async (response) => {
                        this.setQueryResolved();
                        if(!response){
                            throw Error('No backoffice order!');
                        }
                        if(data.is_invoice){
                            await orderRepository.sendInvoiceData(response.id, response.number, data.invoice);
                        }
                        this.paymentUrl = response.payment.payment.appUrl;
                        this.paymentFormMethod = 'GET';
                        this.paymentParams = null;

                        //tracking
                        trackGA('purchase', {
                            'transaction_id': response.number,
                            'value': parseInt(this.totalCost, 10)/100,
                            'currency': data.currency,
                            'shipping': 0,
                            'items': [
                                {
                                    name: data.search_data.start_place_name + ' - ' + data.search_data.end_place_name,
                                    brand: 'follow_me',
                                    category: 'polaczenia_liniowe',
                                    quantity: data.passengers.length,
                                    price: parseInt(this.totalCost, 10)/100,
                                },
                            ],
                        });
                        trackFB('Purchase', {value: parseInt(this.totalCost, 10)/100, currency: data.currency});
                        trackMS('PRODUCT_PURCHASE', {'ecomm_prodid':response.number,'ecomm_pagetype':'PURCHASE'});

                        this.$nextTick(() => {
                            this.$store.dispatch('airportTransfersSearch/reset');
                            this.$refs.paymentForm.submit();
                        });
                    }).catch((error) => {
                        this.setQueryResolved();
                        this.isSending = false;
                        console.error(error);
                        console.error(this.backofficeError);
                        if(this.backofficeError) {
                            this.backofficeError.forEach((error) => {
                                toastr.error(this.$t('validation.' + error.message));
                            });
                        }
                        else{
                            toastr.error(this.$t('order_error'));
                        }
                        //log error with sentry with response details
                        Sentry.withScope(scope => {
                            scope.setExtras({
                                error: error,
                                response: error.response?error.response:this.backofficeError,
                                cart: data,
                                tz: Intl.DateTimeFormat().resolvedOptions().timeZone,
                                localTime: new Date().toLocaleString('pl-PL', {timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone}),
                            });
                            Sentry.captureMessage('AT order ERROR', 'critical');
                        });
                    });
                }
            },
            onIsInvoiceInput(event) {
                this.$store.dispatch('airportTransfersSearch/setIsInvoice', event.target.checked);
            },

            onConsentChange(consent, event) {
                this.$set(consent, 'checked', event.target.checked);
            },

            markAllConsents(event) {
                this.consents.forEach((consent) => {
                    this.$set(consent, 'checked', event.target.checked);
                });
            },

            validatePhone() {
                let valid = true;

                if (this.purchaser.phone && this.purchaser.phone.length < 11) {
                    toastr.error(this.$t('airport_transfers.pages.phone_length_error'));
                    valid = false;
                }

                if (this.purchaser.phone[0] !== '+') {
                    toastr.error(this.$t('phone_has_to_start_with_plus'));
                    valid = false;
                }

                return valid;
            },

            validateZipCode() {
                let valid = true;

                if (this.isInvoice && this.invoice.zip.length < 4) {
                    toastr.error(this.$t('airport_transfers.pages.zip_length_error'));
                    valid = false;
                }

                return valid;
            },
        },
    };
</script>
